// src/pages/Contact.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  SimpleGrid,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  Icon,
  HStack,
  useToast,
  useColorModeValue,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Flex,
  Image,
} from '@chakra-ui/react';
import { FaEnvelope, FaPhone, FaMapMarkerAlt } from 'react-icons/fa';
import Header from '../components/Header';
import Footer from '../components/Footer';
import emailjs from '@emailjs/browser'; // Precisará instalar: npm install @emailjs/browser
import { useLocation } from 'react-router-dom';

const ContactInfo = ({ icon, title, content }) => {
  return (
    <HStack spacing={4} align="flex-start">
      <Icon as={icon} w={6} h={6} color="brand.primary" />
      <Box>
        <Text fontWeight="bold" color="brand.primary">
          {title}
        </Text>
        <Text color="gray.600">{content}</Text>
      </Box>
    </HStack>
  );
};

const Contact = () => {
  const toast = useToast();
  const location = useLocation();
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formSuccess, setFormSuccess] = useState(false);

  // Rolar para o topo quando a página for carregada
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Inicializar o EmailJS
  useEffect(() => {
    emailjs.init("jjk8VUZ4-JqjUmPUm");
  }, []);

  const handleChange = (e) => {
    setFormState({
      ...formState,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Configuração do EmailJS com suas credenciais
      const templateParams = {
        from_name: formState.name,
        from_email: formState.email,
        subject: formState.subject,
        message: formState.message,
      };
      
      // Envio do email usando suas credenciais
      await emailjs.send(
        'service_wtggjrl',
        'template_cgw2vgu',
        templateParams,
        'jjk8VUZ4-JqjUmPUm'
      );
      
      // Em caso de sucesso
      setFormSuccess(true);
      setFormState({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
      
      toast({
        title: 'Mensagem enviada!',
        description: 'Entraremos em contato em breve.',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } catch (error) {
      console.error('Erro ao enviar email:', error);
      toast({
        title: 'Erro ao enviar mensagem',
        description: 'Por favor, tente novamente ou entre em contato diretamente pelo e-mail.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Header />
      <Box pt="100px" pb="60px" minH="100vh" bg={useColorModeValue('gray.50', 'gray.900')}>
        <Container maxW="1200px">
          <VStack spacing={12} align="stretch">
            <VStack spacing={4} textAlign="center">
              <Heading 
                as="h1" 
                fontSize={{ base: '3xl', md: '4xl', lg: '5xl' }}
                bgGradient="linear(to-r, brand.primary, brand.secondary)"
                bgClip="text"
              >
                Entre em Contato
              </Heading>
              <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.600" maxW="800px">
                Estamos aqui para ajudar. Envie uma mensagem e retornaremos o mais breve possível.
              </Text>
            </VStack>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10}>
              {/* Informações de contato */}
              <Box
                bg="white"
                p={8}
                borderRadius="lg"
                boxShadow="md"
                h="fit-content"
              >
                <VStack spacing={8} align="stretch">
                  <Text fontSize="xl" fontWeight="bold" color="brand.primary">
                    Informações de Contato
                  </Text>
                  
                  <VStack spacing={6} align="stretch">
                    <ContactInfo 
                      icon={FaEnvelope} 
                      title="E-mail" 
                      content="drawgoodscontato@gmail.com" 
                    />
                    <ContactInfo 
                      icon={FaPhone} 
                      title="Telefone" 
                      content="+55 (82) 99105-0175" 
                    />
                    <ContactInfo 
                      icon={FaMapMarkerAlt} 
                      title="Localização" 
                      content="Maceió, AL - Brasil" 
                    />
                  </VStack>

                </VStack>
              </Box>
              
              {/* Formulário de contato */}
              <Box
                bg="white"
                p={8}
                borderRadius="lg"
                boxShadow="md"
              >
                {formSuccess ? (
                  <Alert
                    status="success"
                    variant="subtle"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    textAlign="center"
                    height="300px"
                    borderRadius="lg"
                  >
                    <AlertIcon boxSize="40px" mr={0} />
                    <AlertTitle mt={4} mb={1} fontSize="lg">
                      Mensagem enviada com sucesso!
                    </AlertTitle>
                    <AlertDescription maxWidth="sm">
                      Obrigado por entrar em contato. Responderemos sua mensagem em breve.
                    </AlertDescription>
                    <Button
                      mt={6}
                      colorScheme="green"
                      onClick={() => setFormSuccess(false)}
                    >
                      Enviar nova mensagem
                    </Button>
                  </Alert>
                ) : (
                  <VStack spacing={6} as="form" onSubmit={handleSubmit}>
                    <Text fontSize="xl" fontWeight="bold" color="brand.primary" alignSelf="flex-start">
                      Envie uma Mensagem
                    </Text>
                    
                    <FormControl isRequired>
                      <FormLabel htmlFor="name">Nome</FormLabel>
                      <Input
                        id="name"
                        name="name"
                        placeholder="Seu nome completo"
                        value={formState.name}
                        onChange={handleChange}
                        borderRadius="md"
                        focusBorderColor="brand.primary"
                      />
                    </FormControl>
                    
                    <FormControl isRequired>
                      <FormLabel htmlFor="email">E-mail</FormLabel>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="seu.email@exemplo.com"
                        value={formState.email}
                        onChange={handleChange}
                        borderRadius="md"
                        focusBorderColor="brand.primary"
                      />
                    </FormControl>
                    
                    <FormControl isRequired>
                      <FormLabel htmlFor="subject">Assunto</FormLabel>
                      <Input
                        id="subject"
                        name="subject"
                        placeholder="Assunto da mensagem"
                        value={formState.subject}
                        onChange={handleChange}
                        borderRadius="md"
                        focusBorderColor="brand.primary"
                      />
                    </FormControl>
                    
                    <FormControl isRequired>
                      <FormLabel htmlFor="message">Mensagem</FormLabel>
                      <Textarea
                        id="message"
                        name="message"
                        placeholder="Digite sua mensagem aqui..."
                        value={formState.message}
                        onChange={handleChange}
                        rows={5}
                        borderRadius="md"
                        focusBorderColor="brand.primary"
                      />
                    </FormControl>
                    
                    <Button
                      type="submit"
                      bgGradient="linear(to-r, brand.primary, brand.secondary)"
                      color="white"
                      size="lg"
                      w="full"
                      borderRadius="full"
                      _hover={{
                        transform: 'translateY(-2px)',
                        boxShadow: '0 5px 15px rgba(165, 180, 232, 0.4)',
                      }}
                      isLoading={isSubmitting}
                      loadingText="Enviando..."
                    >
                      Enviar Mensagem
                    </Button>
                  </VStack>
                )}
              </Box>
            </SimpleGrid>

            {/* Seção decorativa no lugar do mapa */}
            <Box
              mt={10}
              borderRadius="lg"
              overflow="hidden"
              boxShadow="md"
              bg="white"
              p={{ base: 6, md: 10 }}
            >
              <Flex 
                direction={{ base: "column", md: "row" }}
                align="center"
                justify="space-between"
                gap={8}
              >
                <Box maxW={{ base: "100%", md: "60%" }}>
                  <Heading as="h3" size="lg" color="brand.primary" mb={4}>
                    Transforme suas memórias em arte para colorir
                  </Heading>
                  <Text color="gray.600" fontSize="md">
                    A Draw Your Goods está aqui para ajudar você a criar experiências únicas e significativas 
                    a partir de suas fotos favoritas. Ficaremos felizes em responder suas dúvidas e 
                    ajudar você a escolher o pacote ideal para suas necessidades.
                  </Text>
                  <Button
                    as="a"
                    href="/#precos"
                    mt={6}
                    size="md"
                    bgGradient="linear(to-r, brand.primary, brand.secondary)"
                    color="white"
                    _hover={{
                      transform: 'translateY(-2px)',
                      boxShadow: '0 5px 15px rgba(165, 180, 232, 0.4)',
                    }}
                    borderRadius="full"
                  >
                    Ver nossos pacotes
                  </Button>
                </Box>
              </Flex>
            </Box>
          </VStack>

          {/* SEO - Schema.org markup for Google */}
          <script type="application/ld+json">{`
            {
              "@context": "https://schema.org",
              "@type": "ContactPage",
              "name": "Contato - Draw Your Goods",
              "description": "Entre em contato com a equipe da Draw Your Goods para dúvidas, sugestões ou suporte.",
              "publisher": {
                "@type": "Organization",
                "name": "Draw Your Goods",
                "logo": "https://www.drawyourgoods.com.br/logo.png",
                "contactPoint": {
                  "@type": "ContactPoint",
                  "telephone": "+5582991050175",
                  "contactType": "customer service",
                  "email": "drawgoodscontato@gmail.com",
                  "availableLanguage": "Portuguese"
                }
              },
              "mainEntity": {
                "@type": "WebPage",
                "name": "Página de Contato",
                "url": "https://www.drawyourgoods.com.br/contato"
              }
            }
          `}</script>
        </Container>
      </Box>
      <Footer />
    </>
  );
};

export default Contact;